import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { Badge } from '@/components/UI/Badge';
import { Modal } from '@/components/UI/Modal';
import { Input } from '@/components/UI/Input';
import { Select } from '@/components/UI/Select';
import { Textarea } from '@/components/UI/Textarea';
import type { SamAction, SamEditableField, SamPresentation } from '@/components/SAM/samActionTypes';
import { useGetClientConfigQuery } from '@/redux/internal-services/agentApi';
import { isoToLocalParts, localPartsToIso } from '@/utils/isoLocalDateTime';
import {
  ActionBoltIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  PhoneIcon,
  PinIcon,
  SpinnerIcon,
  XCircleIcon,
} from '@/components/chat/ChatIcons';

export interface ConfirmMessage {
  kind: 'confirm';
  id: string;
  action: SamAction;
  /** Not set by confirmation cards today — kept optional so this type structurally matches the
   *  other workspace-surface messages (see ChatPage's turn-matched reply-text lookup). */
  messageId?: string;
  resolved?: 'approved' | 'rejected';
  /** The tool's actual outcome once it has run — arrives separately from `resolved` (which only
   *  reflects the user's click). Undefined while an approved action is still executing. */
  outcome?: 'success' | 'partial_success' | 'failed' | 'cancelled';
  outcomeMessage?: string;
  outcomeData?: Record<string, unknown> | null;
  /** The card rulebook's decision for this card (envelope `presentation`), e.g. an OVERLAY. */
  presentation?: SamPresentation | null;
  /** The user closed the decided card — it no longer takes the workspace. */
  dismissed?: boolean;
}

/** Backend sends titles like "Confirm — add new property"; strip the prefix and sentence-case
 *  the remainder so the card reads as a clean action label ("Add new property"). */
const formatTitle = (raw: string | undefined, toolName: string | undefined, fallback: string): string => {
  const src = (raw || toolName || '').trim();
  if (!src) return fallback;
  const stripped = src.replace(/^\s*confirm\s*[—:-]\s*/i, '').replace(/_/g, ' ').trim();
  if (!stripped) return fallback;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v));

const formatKey = (k: string) =>
  k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** A tool arg's value is usually a primitive, but a free-form dict arg (e.g. update_engagement's
 *  `updates`) arrives as a nested object — `String(v)` on that yields the literal "[object
 *  Object]" (JS's default, no-op Object.prototype.toString), not the field's actual content.
 *  Render it instead as its own "key: value" pairs so the card still shows what's actually being
 *  changed. */
const stringifyValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(stringifyValue).join(', ');
  if (v !== null && typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => `${formatKey(k)}: ${stringifyValue(val)}`)
      .join(', ');
  }
  return String(v);
};

/** The PropertyAddress component args create_engagement's confirm sends, one per persisted DB
 *  column, in postal order so the composed lines below read naturally. Any arg NOT listed here
 *  stays in the generic detail grid, so other tools' confirm cards are unaffected. */
const ADDRESS_KEYS = [
  'street_number', 'pre_direction', 'street_name', 'street_suffix', 'post_direction',
  'unit_type', 'unit_number', 'city', 'state', 'postal_code', 'postal_code_plus4', 'country',
] as const;
const ADDRESS_KEY_SET = new Set<string>(ADDRESS_KEYS);

const join = (parts: Array<string | undefined>, sep = ' ') =>
  parts.map((p) => (p ?? '').trim()).filter(Boolean).join(sep);

/** Fold the component args into one written address line. Returns null when this isn't an address
 *  card (no street and no city), leaving the generic grid in charge. */
const composeAddress = (args: Record<string, unknown>): string | null => {
  const s = (k: string) => {
    const v = args[k];
    return v === null || v === undefined ? '' : String(v).trim();
  };
  const street = join([
    s('street_number'), s('pre_direction'), s('street_name'), s('street_suffix'), s('post_direction'),
  ]);
  const city = s('city');
  if (!street && !city) return null;
  const unit = join([s('unit_type'), s('unit_number')]);
  const zip = join([s('postal_code'), s('postal_code_plus4')], '-');
  return join([street, unit, city, join([s('state'), zip]), s('country')], ', ');
};

/** The contact's phone rides along in the contact cell rather than getting a cell of its own. */
const CONTACT_PHONE_KEY = 'contact_phone';

/** Per tool: args never shown on the confirm card (create_engagement's lead source). */
const HIDDEN_ARGS: Record<string, ReadonlySet<string>> = {
  create_engagement: new Set(['source']),
};

/** A type/category value renders as a badge rather than plain text. Guessed from the arg's key
 *  name so this stays generic across tools (create_engagement, create_contact, etc.) rather than
 *  hardcoding one fixed set of fields. */
const isTypeField = (key: string) => /type/i.test(key);

/** Resolved outcome state, folding msg.resolved + msg.outcome into one thing to render — the
 *  card's footer swaps between the Cancel/Confirm buttons and exactly one of these once the
 *  user has decided. */
type ResolvedState = 'cancelled' | 'running' | 'success' | 'partial' | 'failed' | 'expired';

/** ResolvedState value -> i18n key under chat.confirm.* (the 'success' state reads as "Done"). */
const STATE_LABEL_KEY: Record<ResolvedState, string> = {
  cancelled: 'chat.confirm.cancelled',
  running: 'chat.confirm.running',
  success: 'chat.confirm.done',
  partial: 'chat.confirm.partial',
  failed: 'chat.confirm.failed',
  expired: 'chat.confirm.expired',
};

const resolveState = (msg: ConfirmMessage, expired: boolean): ResolvedState | null => {
  // The backend stopped waiting and no result reached this card (e.g. it was offline) — the
  // buttons would send a decision nobody reads.
  if (!msg.resolved) return expired ? 'expired' : null;
  if (msg.resolved === 'rejected') return 'cancelled';
  if (!msg.outcome) return 'running';
  if (msg.outcome === 'failed') return 'failed';
  if (msg.outcome === 'cancelled') return 'cancelled';
  // The main action ran but a secondary step did not (e.g. engagement created, contact not
  // added) — the outcome message below says what was not done.
  if (msg.outcome === 'partial_success') return 'partial';
  return 'success';
};

/** Seconds left until `expiresAt` (epoch ms), ticking once a second while the card is undecided.
 *  null when the card carries no expiry (older backend) or is already decided. */
const useSecondsLeft = (expiresAt: number | undefined, active: boolean): number | null => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !expiresAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active, expiresAt]);
  if (!active || !expiresAt) return null;
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
};

const formatSeconds = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** One label/value cell of the detail grid. Two per row at every width (these values are short),
 *  and no per-field icon chip — both buy back the vertical space that used to push the footer
 *  buttons out of view on a short viewport. The address block above keeps its pin, where the glyph
 *  actually carries meaning. */
/** One field of the card's edit mode, rendered by the field's declared type. Long text and
 *  free-form notes take the full row; everything else sits two to a row. */
const EditField = ({ field, value, onChange }: {
  field: SamEditableField; value: string; onChange: (v: string) => void;
}) => {
  const wide = field.type === 'textarea';
  const common = { label: field.label, required: field.required, wrapperClassName: wide ? 'col-span-2' : undefined };
  if (field.type === 'textarea') {
    return <Textarea {...common} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'datetime') {
    // The browser's own date + time picker: it opens as a native overlay, so the card's scroll area
    // cannot clip it (the meeting form's popover pickers were cut off here). One ISO value with the
    // local UTC offset goes back to the backend.
    const { date, time } = isoToLocalParts(value);
    return (
      <Input
        {...common}
        wrapperClassName="col-span-2"
        type="datetime-local"
        value={date ? `${date}T${time}` : ''}
        onChange={(e) => {
          const [d, tm] = e.target.value.split('T');
          onChange(localPartsToIso(d ?? '', tm ?? ''));
        }}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <Select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
        {!field.required && <option value="" />}
        {(field.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </Select>
    );
  }
  return (
    <Input
      {...common}
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      inputMode={field.type === 'number' ? 'decimal' : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const DetailCell = ({ name, value, phone }: { name: string; value: string; phone?: string }) => {
  const { t } = useTranslation();
  return (
  <div className="min-w-0 py-2.5">
    {/* Known arg names have a translated label (chat.confirm.fields.*); any other tool's arg falls
        back to its key in Title Case. */}
    <div className="eyebrow">{t(`chat.confirm.fields.${name}`, { defaultValue: formatKey(name) })}</div>
    {isTypeField(name) ? (
      <Badge variant="accent" className="mt-1 gap-1.5 py-0.5 text-sm font-semibold">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
        {value}
      </Badge>
    ) : (
      <div className="mt-0.5 break-words text-sm font-medium leading-snug text-ui-text">{value}</div>
    )}
    {phone && (
      <div className="mt-1 flex items-center gap-1.5 text-xs text-ui-text2">
        <PhoneIcon className="h-3 w-3 shrink-0 text-icon" />
        <span className="break-all">{phone}</span>
      </div>
    )}
  </div>
  );
};

/** Renders full-width in the workspace panel (see ChatPage's surfacePanel) as a self-contained
 *  card — never a chat bubble. The panel already provides the scroll container + padding. */
export const ConfirmCard = ({
  msg,
  onDecide,
  onDismiss,
  variant = 'panel',
}: {
  msg: ConfirmMessage;
  /** `edits` (approved only): fields the user corrected, keyed by the field's `arg` path. */
  onDecide: (d: 'approved' | 'rejected', edits?: Record<string, unknown>) => void;
  /** Close a decided card (offered once the result is in). */
  onDismiss?: () => void;
  /** `panel` = the workspace card (default); `dialog` = a blocking Modal (rulebook OVERLAY). */
  variant?: 'panel' | 'dialog';
}) => {
  const { t } = useTranslation();
  const a = msg.action;
  const args = a.tool_args || {};
  const title = formatTitle(a.title, a.tool_name, t('chat.confirm.defaultTitle'));
  const entries = Object.entries(args).filter(
    ([, v]) => v !== null && v !== undefined && stringifyValue(v).trim() !== '',
  );
  const address = composeAddress(args);
  const contactPhone = args[CONTACT_PHONE_KEY] ? String(args[CONTACT_PHONE_KEY]).trim() : '';
  // The address components are rolled into the one address line, and the phone into the contact
  // cell, so neither may also appear as its own cell in the grid.
  const details = entries.filter(
    ([k]) => k !== CONTACT_PHONE_KEY && !(address && ADDRESS_KEY_SET.has(k))
      && !HIDDEN_ARGS[a.tool_name ?? '']?.has(k),
  );
  const secondsLeft = useSecondsLeft(a.expires_at, !msg.resolved);
  const state = resolveState(msg, secondsLeft === 0);
  const editable = a.editable ?? [];
  // Editable fields are inputs straight away while the card waits — no separate edit step. Once
  // the card is decided the read-only details come back (rebuilt from the saved values).
  const editing = editable.length > 0 && !state;
  const initialDraft = () => Object.fromEntries(editable.map((f) => [f.arg, asText(f.value)]));
  const [draft, setDraft] = useState<Record<string, string>>(initialDraft);
  // A re-delivered / different card (new call_id) starts from its own values.
  useEffect(() => { setDraft(initialDraft()); }, [a.call_id]); // eslint-disable-line react-hooks/exhaustive-deps
  // What the user changed and sent — listed next to the details once confirmed.
  const [sentEdits, setSentEdits] = useState<{ label: string; value: string }[]>([]);
  // Only the fields whose text actually changed go back; the backend checks and re-runs with them.
  const changedEdits = () => Object.fromEntries(
    editable
      .filter((f) => (draft[f.arg] ?? '') !== asText(f.value))
      .map((f) => [f.arg, draft[f.arg] ?? '']),
  );
  const missingRequired = editing && editable.some((f) => f.required && !(draft[f.arg] ?? '').trim());
  const confirm = () => {
    const edits = changedEdits();
    setSentEdits(editable.filter((f) => f.arg in edits).map((f) => ({ label: f.label, value: String(edits[f.arg] ?? '') })));
    onDecide('approved', edits);
  };
  // Read-only details that are NOT also an input (e.g. engagement type next to address inputs).
  const editableKeys = new Set(editable.flatMap((f) => [f.arg.split('.').pop() ?? '', f.arg.split('.')[0] ?? '']));
  const editsAddress = editable.some((f) => f.arg.startsWith('property_address.'));
  const readOnlyDetails = details.filter(([k]) => !editableKeys.has(k));
  // A finished card closes by itself after a few seconds (sam_tool_confirmation.result_close_seconds):
  // done, done with one thing left, and cancelled — the user pressed Cancel, so they already know.
  // Failed and expired stay until closed: the user has to read why nothing happened.
  const { data: clientConfig } = useGetClientConfigQuery();
  const closeAfterMs = (clientConfig?.confirmation?.result_close_seconds ?? 4) * 1000;
  // Through a ref: the parent passes a new inline onDismiss every render, which would restart the timer.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  useEffect(() => {
    if (state !== 'success' && state !== 'partial' && state !== 'cancelled') return undefined;
    const timer = window.setTimeout(() => onDismissRef.current?.(), closeAfterMs);
    return () => window.clearTimeout(timer);
  }, [state, closeAfterMs]);

  const header = (
    <div className="flex shrink-0 items-start gap-3 px-5 pb-3 pt-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-accent-soft text-accent-text">
        <ActionBoltIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <h1 className="text-base font-semibold leading-tight tracking-tight text-ui-text">{title}</h1>
        <p className="mt-0.5 text-sm leading-snug text-ui-text2">{t('chat.confirm.subtitle')}</p>
      </div>
    </div>
  );
  const detailsBlock = (
    (address || details.length > 0) && (
      <div className="no-scrollbar mx-5 min-h-0 flex-1 overflow-y-auto border-t border-ui-border">
        {address && (
          <div
            className={`flex items-start gap-3 py-3 ${details.length > 0 ? 'border-b border-ui-border' : ''}`}
          >
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-chip bg-accent-soft text-accent-text">
              <PinIcon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="eyebrow">{t('chat.confirm.address')}</div>
              <div className="mt-0.5 break-words text-sm font-medium leading-snug text-ui-text">
                {address}
              </div>
            </div>
          </div>
        )}

        {details.length > 0 && (
          <div className="grid grid-cols-2 gap-x-4">
            {details.map(([k, v], i) => (
              <div
                key={k}
                // Border on every cell except those in the last row — with a fixed 2 columns the
                // last row starts at the largest even index, which `last:`/`nth-last-child` can't
                // express when the count is odd.
                className={i < details.length - (details.length % 2 === 0 ? 2 : 1)
                  ? 'border-b border-ui-border'
                  : ''}
              >
                <DetailCell
                  name={k}
                  value={stringifyValue(v)}
                  phone={k === 'contact' ? contactPhone : undefined}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  );
  const editedBlock = sentEdits.length > 0 && (
    <div className="mx-5 mt-2 shrink-0 rounded-control bg-accent-soft px-3 py-2 text-xs text-accent-text">
      <span className="font-semibold">{t('chat.confirm.edited')}</span>{' '}
      {sentEdits.map((e) => `${e.label}: ${e.value || '—'}`).join(' · ')}
    </div>
  );
  const editBlock = (
    <div className="no-scrollbar mx-5 min-h-0 flex-1 overflow-y-auto border-t border-ui-border pt-3">
      <div className="grid grid-cols-2 gap-x-3 gap-y-3">
        {editable.map((f) => (
          <EditField
            key={f.arg}
            field={f}
            value={draft[f.arg] ?? ''}
            onChange={(v) => setDraft((d) => ({ ...d, [f.arg]: v }))}
          />
        ))}
      </div>
      {/* The address is edited through its own inputs above; other details stay read-only. */}
      {(readOnlyDetails.length > 0 || (address && !editsAddress)) && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 border-t border-ui-border">
          {address && !editsAddress && (
            <div className="col-span-2"><DetailCell name="address" value={address} /></div>
          )}
          {readOnlyDetails.map(([k, v]) => (
            <DetailCell key={k} name={k} value={stringifyValue(v)} phone={k === 'contact' ? contactPhone : undefined} />
          ))}
        </div>
      )}
    </div>
  );
  const footer = (
    state ? (
      // Spacing as padding on an outer wrapper: the dialog body is a scroll container, which drops a
      // last child's bottom margin (the box sat flush on the dialog's bottom edge).
      <div className="shrink-0 px-5 pb-4 pt-3">
      <div className="flex items-center gap-2.5 rounded-control bg-ui-subtle p-3.5">
        {(state === 'cancelled' || state === 'expired') && <XCircleIcon className="h-5 w-5 shrink-0 text-ui-text3" />}
        {state === 'running' && <SpinnerIcon className="h-5 w-5 shrink-0 text-ui-text2" />}
        {state === 'success' && <CheckCircleIcon className="h-5 w-5 shrink-0 text-success" />}
        {state === 'partial' && <AlertCircleIcon className="h-5 w-5 shrink-0 text-warning" />}
        {state === 'failed' && <AlertCircleIcon className="h-5 w-5 shrink-0 text-danger" />}
        <div className="min-w-0">
          <p
            className={`text-sm font-medium leading-tight ${
              state === 'success'
                ? 'text-success'
                : state === 'partial'
                  ? 'text-warning-text'
                  : state === 'failed'
                    ? 'text-danger'
                    : 'text-ui-text2'
            }`}
          >
            {t(STATE_LABEL_KEY[state])}
          </p>
        </div>
        {/* The dialog already has its × in the title bar — one close control is enough there. */}
        {state !== 'running' && onDismiss && variant !== 'dialog' && (
          <Button variant="ghost" size="sm" className="ml-auto shrink-0" onClick={onDismiss}>
            {t('chat.confirm.close')}
          </Button>
        )}
      </div>
      </div>
    ) : (
      <div className="shrink-0 px-5 pb-4 pt-3.5">
        <div className="flex gap-2.5">
          <Button variant="outline" className="flex-1" onClick={() => onDecide('rejected')}>
            {t('chat.confirm.cancel')}
          </Button>
          {/* Sends whatever the user changed in the inputs (nothing changed = a plain confirm). */}
          <Button className="flex-1" disabled={missingRequired} onClick={confirm}>
            <CheckIcon className="h-3.5 w-3.5" />
            {t('chat.confirm.confirm')}
          </Button>
        </div>
        {secondsLeft !== null && (
          <p className="mt-2 text-center text-xs text-ui-text3" aria-live="polite">
            {t('chat.confirm.expiresIn', { time: formatSeconds(secondsLeft) })}
          </p>
        )}
      </div>
    )
  );

  if (variant === 'dialog') {
    // The rulebook asked for a blocking overlay (sensitive-action-confirmation → OVERLAY): the one
    // Modal dialog. Escape / × while undecided = Cancel (an explicit answer the tool is waiting for);
    // ignored while the tool runs; afterwards it closes the result.
    const close = () => {
      if (!state) onDecide('rejected');
      else if (state !== 'running') onDismiss?.();
    };
    return (
      <Modal title={title} onClose={close} closeOnBackdrop={false} centered maxWidthClassName="max-w-md" bodyClassName="space-y-0 p-0">
        <p className="px-5 pt-3 text-sm leading-snug text-ui-text2">{t('chat.confirm.subtitle')}</p>
        {editing ? editBlock : detailsBlock}
        {editedBlock}
        {footer}
      </Modal>
    );
  }

  return (
    // Bounded by the panel, not by a fixed fraction of the viewport: header and footer never
    // shrink, so Cancel/Confirm stay on screen, and only the detail area scrolls — and only when
    // the content genuinely cannot fit, which the compact grid below makes rare.
    <div className="card-workspace mx-auto flex max-h-full w-full max-w-md flex-col overflow-hidden">
      {header}
      {editing ? editBlock : detailsBlock}
      {editedBlock}
      {footer}
    </div>
  );
};

export default ConfirmCard;
